import { useDispatch } from "react-redux";
import { employeesInterface } from ".";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { hideModal, showModal } from "store/modal/modalSlice";
import { useMutation, useQuery } from "react-query";
import { acceptUserAxios, getUserWorkplaces, getUsers } from "services/registration";
import { getCourseData } from "services/elearning";
import { Course } from "types/elearning";
import { downloadCertificates, downloadSingleCertificate } from "services/certificate";
import { useUser } from "hooks/useUser";
import { toast } from "react-toastify";
import { AxiosError, AxiosResponse } from "axios";
import { APIErrors } from "types/api";

type ModalType = 'generateLogin' | 'editData' | 'deleteUser' | 'addNewUser' | 'acceptUser' | 'changeWorkplace' | 'addNewUserSingle' | 'addNewUserMultiple' | 'editWorkplaceData' | 'downloadCertificatesSelect' | null;

export interface Workplace {
    department?: string;
    id: number;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
}

export const useEmployees = () => {
    const [employees, setEmployees] = useState<employeesInterface[]>([]);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showTooltip, setShowTooltip] = useState<number | 'header' | null>(null);
    const [modalType, setModalType] = useState<ModalType>(null);
    // const [filteredEmployees, setFilteredEmployees] = useState<employeesInterface[]>(employees);
    const searchRef = useRef<HTMLInputElement>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [selectedEmployees, setSelectedEmployees] = useState<employeesInterface[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<employeesInterface | null>(null);
    const dispatch = useDispatch();
    const { isAuthenticated, userData } = useUser();
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [page, setPage] = useState<number>(1);
    const [showFirstLogin, setShowFirstLogin] = useState<boolean>(true);
    const [loadMoreIsHide, setLoadMoreIsHide] = useState<boolean>(false);
    const [downloadCertificateModalType, setDownloadCertificateModalType] = useState<'bulk' | 'single' | null>(null);
    const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
    const [downloadModalEmployee, setDownloadModalEmployee] = useState<employeesInterface | null>(null);

    const {
        data: userFromApi = [],
        isLoading,
        error
    } = useQuery(
        ['getUsers', userData?.email, page, refreshCounter],
        () => getUsers({ page }),
        {
            enabled: !!userData?.email,
            staleTime: Infinity,
            cacheTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    const { data: adminWorkplaces = [] } = useQuery(
        ['getUserWorkplaces', userData?.email],
        getUserWorkplaces,
        {
            enabled: !!userData?.email,
            staleTime: Infinity,
        }
    );

    const { data: allCourses = [], isLoading: coursesLoading, error: coursesError } = useQuery<Course[]>(
        ['elearningCourses'],
        () => getCourseData(),
        {
            enabled: !!userData?.email,
            staleTime: Infinity,
            cacheTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    useEffect(() => {
        if (!userFromApi?.length) return;

        setEmployees(prev => {
            const combined = page === 1 ? userFromApi : [...(prev ?? []), ...userFromApi];
            const unique = Array.from(new Map(combined.map(e => [e.id, e])).values());
            return unique;
        });

        setLoadMoreIsHide(userFromApi.length < 20);
    }, [page, userFromApi?.length]);

    const filteredEmployees = useMemo(() => {
        if (!employees) return [];

        return employees.filter(employee => {
            const matchesSearch =
                !searchTerm ||
                employee.first_name?.toLowerCase().includes(searchTerm) ||
                employee.last_name?.toLowerCase().includes(searchTerm) ||
                employee.login?.toLowerCase().includes(searchTerm) ||
                employee.phone?.toLowerCase().includes(searchTerm);

            return matchesSearch;
        });
    }, [employees, searchTerm]);

    const handleChange = (e: { target: { value: string } }) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const searchHandle = () => {
        const value = searchRef.current?.value.toLowerCase() || '';
        setSearchTerm(value);
    };

    const showHandle = (id: number | 'header') => {
        if (id === 'header' && selectedEmployees.length === 0) {
            setShowTooltip(null);
            return;
        }

        setShowTooltip((prev) => (prev === id ? null : id));
    };

    const handleCheckboxChange = (employee: employeesInterface) => {
        setSelectedEmployees((prev) => {
            const isSelected = prev.some(e => e.id === employee.id);
            if (isSelected) {
                return prev.filter(e => e.id !== employee.id);
            } else {
                return [...prev, employee];
            }
        });
    };

    const handleToggleAll = () => {
        if (selectedEmployees.length === filteredEmployees.length) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees(filteredEmployees);
        }
    };

    const sendLogins = () => {
        setModalType('generateLogin');
        dispatch(showModal());
    }

    const deleteUsers = () => {
        setModalType('deleteUser');
        dispatch(showModal());
    }

    useEffect(() => {
        dispatch(hideModal());
    }, []);

    const hideModalHandle = (reloadUsers = false) => {
        setDownloadCertificateModalType(null);
        setSelectedCourseIds([]);
        setDownloadModalEmployee(null);

        dispatch(hideModal());

        if (reloadUsers) {
            setRefreshCounter(prev => prev + 1);
            setSelectedEmployees([]);
        }
    }

    const generateSingleLoginAndPassword = (employee: employeesInterface) => {
        setModalType('generateLogin');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const editData = (employee: employeesInterface) => {
        setModalType('editData');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const deleteUser = (employee: employeesInterface) => {
        setModalType('deleteUser');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const addNewUserShowPopup = () => {
        setModalType('addNewUser');
        dispatch(showModal());
    }

    const acceptserShowPopup = (employee: employeesInterface) => {
        setModalType('acceptUser');
        setSelectedEmployee(employee);
        dispatch(showModal());
    }

    const findWorkplaceId = () => {
        if (adminWorkplaces && Array.isArray(adminWorkplaces) && selectedEmployees.length) {
            const found = adminWorkplaces.find(item =>
                Object.values(item).includes(selectedEmployees[0].workplace_name)
            );
            return found?.id ?? 0;
        }

        return 0;
    };

    const showMoreEmployees = () => {
        setPage((prev) => prev + 1);
    };

    const acceptUser = (employee: employeesInterface) => {
        acceptUserHandler.mutate(employee.id);
    }

    const acceptUserHandler = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        number
    >((data) => acceptUserAxios(data), {
        onSuccess: (data) => {
            if (data.status === 200) {
                toast.success(t('employeeManagement.acceptSuccessMessage'));
                setShowTooltip(null);
                setRefreshCounter(prev => prev + 1);
            }
        },
        onError: (error) => {
            toast.error(t('employeeManagement.acceptErrorMessage'));
            setShowTooltip(null);
        },
    });

    const downloadCertificatesHandler = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        { user_id?: number; user_ids?: number[]; elearning_course_ids: number[] }
    >(downloadCertificates, {
        onSuccess: (response) => {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificates.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('employeeManagement.downloadSuccessMessage'));
        },
        onError: (error) => {
            toast.error(t('employeeManagement.downloadErrorMessage'));
        },
    });

    const downloadSingleCertificateHandler = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        string
    >(downloadSingleCertificate, {
        onSuccess: (response) => {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificate.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('employeeManagement.downloadSuccessMessage'));
        },
        onError: (error) => {
            toast.error(t('employeeManagement.downloadErrorMessage'));
        },
    });
    const prepareCertificateDownloadModal = (type: 'bulk' | 'single', employee?: employeesInterface) => {
        if (type === 'bulk') {
            if (!selectedEmployees.length) {
                toast.error(t('employeeManagement.noEmployeesSelected'));
                return;
            }

            if (selectedEmployees.length > 200) {
                toast.error(t('employeeManagement.tooManyEmployees'));
                return;
            }
        }

        const completedCourseIds = type === 'single' && employee
            ? (employee.elearning_analitics ?? []).filter(x => x.course_is_done === 1).map(x => x.elearning_course_id)
            : Array.from(new Set((selectedEmployees
                .flatMap(e => (e.elearning_analitics ?? []).filter(x => x.course_is_done === 1).map(x => x.elearning_course_id))
            )));

        setSelectedCourseIds(completedCourseIds);
        setDownloadCertificateModalType(type);
        setDownloadModalEmployee(type === 'single' ? employee ?? null : null);
        setModalType('downloadCertificatesSelect');
        dispatch(showModal());
    };

    const downloadCertificatesAction = () => {
        prepareCertificateDownloadModal('bulk');
    };

    const downloadSingleCertificateAction = (employee: employeesInterface) => {
        prepareCertificateDownloadModal('single', employee);
    };

    const cancelDownloadCertificateModal = () => {
        setDownloadCertificateModalType(null);
        setSelectedCourseIds([]);
        setDownloadModalEmployee(null);
        hideModalHandle();
    };

    const confirmDownloadCertificateModal = () => {
        if (!selectedCourseIds.length) {
            toast.error(t('employeeManagement.noCoursesSelected'));
            return;
        }

        if (downloadCertificateModalType === 'bulk') {
            const user_ids = selectedEmployees.map((e) => e.id);
            const payload = selectedEmployees.length === 1
                ? { user_id: selectedEmployees[0].id, elearning_course_ids: selectedCourseIds }
                : { user_ids, elearning_course_ids: selectedCourseIds };

            downloadCertificatesHandler.mutate(payload);
        } else if (downloadCertificateModalType === 'single') {
            if (!downloadModalEmployee) {
                toast.error(t('employeeManagement.noEmployeesSelected'));
                return;
            }

            const payload = {
                user_id: downloadModalEmployee.id,
                elearning_course_ids: selectedCourseIds,
            };

            downloadCertificatesHandler.mutate(payload);
        }

        setDownloadCertificateModalType(null);
        setSelectedCourseIds([]);
        setDownloadModalEmployee(null);
        hideModalHandle();
    };

    const ellipsis = (str: string, max: number) => {
        if (!str) return '';
        return str.length > max ? str.slice(0, max) + '...' : str;
    }

    return {
        navigate,
        t,
        handleChange,
        showHandle,
        handleCheckboxChange,
        handleToggleAll,
        sendLogins,
        deleteUsers,
        hideModalHandle,
        tooltipRef,
        showTooltip,
        selectedEmployees,
        setShowTooltip,
        searchTerm,
        searchHandle,
        searchRef,
        filteredEmployees,
        generateSingleLoginAndPassword,
        modalType,
        editData,
        deleteUser,
        isAuthenticated,
        addNewUserShowPopup,
        adminWorkplaces,
        findWorkplaceId,
        page,
        showMoreEmployees,
        selectedEmployee,
        acceptserShowPopup,
        showFirstLogin,
        setShowFirstLogin,
        loadMoreIsHide,
        setModalType,
        downloadCertificateModalType,
        selectedCourseIds,
        downloadModalEmployee,
        allCourses,
        coursesLoading,
        coursesError,
        setSelectedCourseIds,
        downloadCertificatesAction,
        downloadSingleCertificateAction,
        confirmDownloadCertificateModal,
        cancelDownloadCertificateModal,
        ellipsis
    };
};
